import React, { useEffect, useState } from 'react';
import { FaTimes, FaUserPlus } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import { InternalFieldName } from 'react-hook-form/dist/types/fields';
import { toast } from 'react-toastify';
import {
  InvoiceViewModel,
  InvoiceWorker,
} from '../../models/Invoice/InvoiceViewModel';
import { useAuth } from '../../contexts/auth';
import api from '../../services/api';
import CustomModal from '../CustomModal/CustomModal';
import InvoiceStatusState from '../../enums/InvoiceStatusState';

interface InvoiceWorkersProps {
  invoice: InvoiceViewModel;
  items: InvoiceWorker[];
  refreshWorkers: () => void;
  removeWorker: (invoiceWorker: InvoiceWorker) => void;
}

export type WorkerModel = {
  id: number;
  name: string;
};

interface InvoiceWorkerFormFields {
  worker: number;
}

function addServerErrors<T>(
  errors: { [P in keyof T]?: string[] },
  setError: any
) {
  return Object.keys(errors).forEach((key) => {
    setError(key as keyof T, {
      type: 'server',
      message: errors[key as keyof T]!.join('. '),
    });
  });
}

export default function InvoiceWorkers({
  invoice,
  items,
  refreshWorkers,
  removeWorker,
}: InvoiceWorkersProps) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
  } = useForm<InvoiceWorkerFormFields>({ mode: 'onBlur' });

  const [isLoading, setIsLoading] = useState(true);
  const [formErrors, setFormErrors] = useState<Record<string, any>>([]);
  const [workers, setWorkers] = useState<WorkerModel[]>([]);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const {
    user: { organization },
    isAdmin,
  } = useAuth();

  useEffect(() => {
    let shouldFocus = true;
    Object.keys(formErrors || {}).forEach((field: InternalFieldName) => {
      addServerErrors(formErrors, setError);

      shouldFocus = false;
    });
  }, [formErrors]);

  async function getWorkers() {
    if (organization) {
      setIsLoading(true);
      setIsOpenModal(true);
      const response = await api.get(
        `organizations/${organization.id}/invoices/${invoice.id}/workers/available`
      );
      setWorkers(response.data.data);
      setIsLoading(false);
    }
  }

  const onSubmit = async (data: InvoiceWorkerFormFields) => {
    try {
      await api.post(
        `organizations/${organization.id}/invoices/${invoice.id}/workers`,
        data
      );

      setIsOpenModal(false);
      setWorkers([]);
      refreshWorkers();
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422) {
          setFormErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  };

  return (
    <>
      <span className='h2 d-flex gap-2 align-items-center'>
        Colaboradores
        {isAdmin &&
          items.length > 0 &&
          items.every((item) => !!item.paidAt) && (
            <span className='badge bg-success'>Pago</span>
          )}
      </span>
      <div className='table-responsive'>
        <table className='table'>
          <thead>
            <tr>
              <th>Nome</th>
              <th style={{ width: '20px' }}>&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className='d-flex gap-2 align-items-center'>
                      {item.name}
                      {isAdmin &&
                        (item.paidAt ? (
                          <span
                            className='badge bg-success'
                            data-bs-toggle='tooltip'
                            data-bs-placement='bottom'
                            title={item.paidAt.toLocaleString()}
                          >
                            Pago
                          </span>
                        ) : (
                          <span className='badge bg-secondary'>Não pago</span>
                        ))}
                    </div>
                  </td>
                  <td>
                    {isAdmin &&
                      invoice.statusState !== InvoiceStatusState.Canceled &&
                      invoice.statusState !== InvoiceStatusState.Completed && (
                        <button
                          className='btn btn-danger btn-sm'
                          type='button'
                          onClick={() => removeWorker(item)}
                        >
                          <FaTimes />
                        </button>
                      )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5}>Nenhum registro</td>
              </tr>
            )}
          </tbody>
        </table>
        <div className='text-end'>
          {isAdmin &&
            invoice.statusState !== InvoiceStatusState.Canceled &&
            invoice.statusState !== InvoiceStatusState.Completed && (
              <button
                type='button'
                className='btn btn-secondary btn-sm'
                onClick={() => getWorkers()}
              >
                Adicionar colaborador <FaUserPlus />
              </button>
            )}

          <CustomModal
            modalTitle='Adicionar colaboradores'
            isOpen={isOpenModal}
            setIsOpenModal={setIsOpenModal}
            fullscreenOnMobile={false}
          >
            <form
              style={{
                paddingTop: '1rem',
                paddingBottom: '1rem',
              }}
              onSubmit={handleSubmit(onSubmit)}
            >
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className='form-label'>Selecione o colaborador</label>
              <select
                required
                className='form-select'
                aria-label='Selecione o colaborador'
                {...register('worker', { required: true })}
              >
                <option value=''>Selecione o colaborador</option>
                {workers.map((worker) => (
                  <option value={worker.id} key={worker.id}>
                    {worker.name}
                  </option>
                ))}
              </select>
              {errors.worker && (
                <div className='form-text text-danger'>
                  {errors.worker.message}
                </div>
              )}
              <div className='text-end mt-3'>
                <button type='submit' className='btn btn-primary'>
                  Adicionar
                </button>
              </div>
            </form>
          </CustomModal>
        </div>
      </div>
    </>
  );
}
